import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettingsExamLock } from '../contexts/ExamContext';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { log as diagLog } from '../lib/diag/readyChainLog';

/**
 * T1-C3 — Guard de ruta del candado de examen.
 *
 * Atenuar el ítem del menú tapa el camino que se ve; esto tapa los otros dos:
 * el `navigate('/settings')` de PricingPage, que no pasa por ningún link, y el
 * deep link. Con examen propio en vuelo la pantalla envuelta NO se monta, sale
 * el mismo toast de siempre y el usuario regresa a donde estaba. Sin candado
 * este componente es transparente: renderea a su hijo y no corre nada más.
 *
 * Por qué la decisión se CONGELA al montar (bloqueadoRef): al navegar fuera del
 * simulador, React renderea la ruta nueva ANTES de correr la limpieza que apaga
 * el candado. Leyendo la señal en vivo, ese re-render posterior la vería ya
 * apagada y dejaría entrar a Ajustes por la puerta de atrás. Congelada, el
 * veredicto es el que había en el instante del intento.
 *
 * ── UNA SOLA FUENTE: LA MEMORIA. FIRMA DE CARLOS (2026-08-05) ───────────────
 *
 *   «Si un usuario deja un examen y queda corriendo el tiempo, esto no debe
 *    impedirle poder entrar a ajustes. El bloqueo a ajustes solamente es
 *    mientras estás en esa pantalla del examen o adaptive.»
 *
 * Este guard preguntaba antes por su cuenta cuando NADIE había registrado
 * superficie: en el build de app le leía la bodega local
 * (`hasAnyOwnInFlightExam`) y en la web mandaba una sonda
 * —`GET /study/exam/in-flight?scope=any`—. Las dos consultas se van, y con
 * ellas la fase de resolver.
 *
 * POR QUÉ SE VAN. El mini-censo del 2026-08-05 (`censo_candado`) reconstruyó el
 * caso: examen arrancado en el cel, kill de la app, reapertura. La memoria de
 * React muere con el proceso, pero la fila de `local_sessions` no; y el
 * predicado local de "en vuelo" (`findOwnInFlightExam`, `examResume.js:132`) no
 * mira el `deadline_at` — "en vuelo" ahí significa sólo "nadie la cerró". Así
 * que un examen VENCIDO y no sellado contestaba "sí" para siempre, desde una
 * pantalla que no tenía nada que ver con el examen, con el ítem del menú sin
 * atenuar (ése sí mira la memoria) y sin ninguna salida visible. El candado
 * dejaba de ser un candado y era una trampa.
 *
 * LA REGLA NUEVA, entera: el veredicto sale del candado de memoria y de nada
 * más. Las tres pantallas de estudio lo prenden al montar y lo apagan al
 * desmontar, así que el bloqueo dura exactamente lo que dura la estadía en la
 * pantalla — ni un instante más. Consecuencias, todas buscadas:
 *
 *   · Tras una recarga o un kill la memoria arranca vacía y Ajustes ABRE, haya
 *     lo que haya en la bodega o en Mongo.
 *   · Parado en el simulador, el bloqueo y su aviso son los de siempre.
 *   · Al salir de la pantalla se libera solo, sin esperar a que nadie selle
 *     nada.
 *
 * Y lo que se pierde, dicho sin adornos: con el examen corriendo, una recarga
 * dentro de la misma sesión deja entrar a Ajustes. Es el precio, y es el lado
 * barato — este candado es UX (evitar que cambies idioma o plan a media hoja),
 * no seguridad: el examen sigue corriendo en el servidor pase lo que pase, así
 * que un falso negativo cuesta una molestia y un falso positivo costaba dejar a
 * un usuario sin sus Ajustes indefinidamente.
 */

// La línea de navegación que el censo pidió (HUECO #3: "no hay línea de
// NAVEGACIÓN en el visor... sin eso, B-M1, B-M2 y B-M3 no se pueden separar").
// Con esto, un rebote injusto futuro JAMÁS es silencioso. Mismo contrato que los
// demás escritores: no-op total en web, jamás lanza.
//
// La ruta sale de `window.location`, no de `useLocation`: este guard sólo la
// quiere para ETIQUETAR una línea de diagnóstico, y colgarlo del router por eso
// sería pagar un acoplamiento (y un re-render por navegación) a cambio de nada.
function rutaActual() {
  try {
    return window.location?.pathname ?? null;
  } catch (e) { return null; }
}

// `fuente` quedó con un solo valor posible —'memoria'— y se sigue escribiendo
// porque es lo que hace legible la línea: quien lea un `NAV.GUARD` en el visor
// tiene que poder afirmar, sin abrir el código, que el rebote lo decidió una
// pantalla montada y no una consulta a la bodega ni a la red. Las fuentes
// 'local' y 'sonda' se fueron con las consultas.
function logGuard(veredicto) {
  if (!IS_APP_BUILD) return;
  try {
    diagLog('NAV.GUARD', { ruta: rutaActual(), veredicto, fuente: 'memoria' });
  } catch (e) { /* el visor jamás decide una navegación */ }
}

export default function ExamLockedRoute({ children }) {
  const { isSettingsLocked, blockSettings } = useSettingsExamLock();
  const navigate = useNavigate();

  // El veredicto del INSTANTE del intento, congelado (ver la cabecera). Sin
  // examen propio registrado en memoria no hay nada que preguntarle a nadie:
  // el guard es transparente en el mismo commit, sin pantalla en blanco.
  const bloqueadoRef = useRef(isSettingsLocked);
  const firedRef = useRef(false);

  // `blockSettings` estrena identidad en cada render (no está memoizada), así
  // que el arreglo de deps no puede garantizar el "una sola vez": quien lo
  // garantiza es `firedRef` — un toast por intento, no por render.
  const bloquear = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    blockSettings();
    // `idx` es el contador de historial de react-router. > 0 significa que hay a
    // dónde volver; si no lo hay (entrada en frío) se cae al dashboard, que es
    // la casa de todo usuario autenticado.
    const idx = window.history.state?.idx;
    if (typeof idx === 'number' && idx > 0) navigate(-1);
    else navigate('/dashboard', { replace: true });
  }, [blockSettings, navigate]);

  useEffect(() => {
    if (bloqueadoRef.current) {
      logGuard('bloquea');
      bloquear(); return;
    }
    logGuard('pasa');
  }, [bloquear]);

  if (bloqueadoRef.current) return null;
  return children;
}
